'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  AdvancedMarker,
  useAdvancedMarkerRef,
} from '@vis.gl/react-google-maps';
import { useClickOutside } from 'src/hooks/common/useClickOutside';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { MarkerCircle } from '../MarkerCircle';
import { PulseButton } from 'src/components/PulseUI/PulseButton';
import { ProviderPopupDetails } from 'src/components/Maps/ProviderPopupDetails';
import getProviderPopupMetrics from '../getProviderPopupMetrics';
import { useOpenProviderProfile } from 'src/hooks/drawers/useOpenProviderProfile';

type TreeMarkerProps = {
  feature: any;
  position: any;
  featureId: number;
  size: number;
  sizeAsText: string;
  isHovered?: boolean;
  onMarkerClick?: (marker: any, featureId: number) => void;
  onMouseOver?: (clusterId: number) => void;
  onMouseOut?: () => void;
  addToList?: (details: any) => void;
};

const FeatureMarker = ({
  feature,
  position,
  featureId,
  size,
  sizeAsText,
  isHovered = false,
  onMarkerClick,
  onMouseOut,
  onMouseOver,
  addToList,
}: TreeMarkerProps) => {
  const openProviderProfile = useOpenProviderProfile();
  const [markerRef, marker] = useAdvancedMarkerRef();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const details = feature?.properties?.details;
  const isPoint = Boolean(details);

  // Clicking (or pressing Enter on the focused marker) opens the popover too, for
  // touch and keyboard users who have no hover.
  const handleMarkerClick = useCallback(() => {
    if (isPoint) setIsPopoverOpen(true);
    if (marker) onMarkerClick?.(marker, featureId);
  }, [isPoint, onMarkerClick, marker, featureId]);

  const updatePopoverPosition = useCallback(() => {
    const markerNode = marker?.element as HTMLElement | null;
    const popoverNode = popoverRef.current;

    if (markerNode && popoverNode) {
      const markerRect = markerNode.getBoundingClientRect();
      const popoverHeight = popoverNode.offsetHeight;
      const popoverWidth = popoverNode.offsetWidth;

      const top = markerRect.top + window.scrollY - popoverHeight - 8;
      const left =
        markerRect.left +
        window.scrollX +
        markerRect.width / 2 -
        popoverWidth / 2;

      setPopoverPosition({ top, left });
    }
  }, [marker]);

  useEffect(() => {
    if (isPopoverOpen) {
      requestAnimationFrame(() => {
        updatePopoverPosition();
      });

      window.addEventListener('scroll', updatePopoverPosition, {
        capture: true,
        passive: true,
      });
      window.addEventListener('resize', updatePopoverPosition, {
        passive: true,
      });

      return () => {
        window.removeEventListener('scroll', updatePopoverPosition, {
          capture: true,
          passive: true,
        } as any);
        window.removeEventListener('resize', updatePopoverPosition, {
          passive: true,
        } as any);
      };
    } else {
      setPopoverPosition(null);
    }
  }, [isPopoverOpen, updatePopoverPosition]);

  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (leaveTimerRef.current) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    if (isPoint && onMouseOver) {
      onMouseOver(featureId);
      setIsPopoverOpen(true);
    }
  };

  const handleMouseLeave = () => {
    leaveTimerRef.current = setTimeout(() => {
      if (!popoverRef.current?.matches(':hover')) {
        setIsPopoverOpen(false);
        onMouseOut?.();
      }
      leaveTimerRef.current = null;
    }, 100);
  };

  const handlePopoverMouseLeave = (e: React.MouseEvent) => {
    const popoverRect = popoverRef.current?.getBoundingClientRect();
    const markerNode = marker?.element as HTMLElement | null;
    const markerRect = markerNode?.getBoundingClientRect();

    if (popoverRect && markerRect) {
      const mouseY = e.clientY;
      if (mouseY > popoverRect.bottom && mouseY < markerRect.top) {
        return;
      }
    }

    setIsPopoverOpen(false);
    onMouseOut?.();
  };

  useClickOutside(popoverRef, () => setIsPopoverOpen(false));

  // Escape dismisses the popover (also the keyboard route out of it).
  useEffect(() => {
    if (!isPopoverOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsPopoverOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPopoverOpen]);

  const handleAddToList = () => {
    if (!addToList || !details) return;
    addToList([details]);
    setIsPopoverOpen(false);
  };

  const providerId = details?.provider_id;
  const contentType = details?.content_type || details?.type;
  const name = details?.name;
  const specialty = details?.specialty;
  const addressLine1 = details?.address_line_1;
  const addressLine2 = details?.address_line_2;
  const city = details?.city;
  const state = details?.state;

  const popoverContent = isPopoverOpen && isPoint && name && (
    <div
      ref={popoverRef}
      onMouseLeave={handlePopoverMouseLeave}
      // The popover is portaled but React still bubbles its clicks to the marker,
      // which would reopen it right after Add to List closes it.
      onClick={(event) => event.stopPropagation()}
      className="fixed w-64 z-[1000] overflow-visible"
      style={{
        top: `${popoverPosition?.top ?? 0}px`,
        left: `${popoverPosition?.left ?? 0}px`,
        opacity: popoverPosition ? 1 : 0,
        transition: 'opacity 0.1s ease-in-out',
      }}
      data-testid={`feature-popover-${featureId}`}
    >
      <div
        className="absolute h-8 w-8 left-1/2 bottom-0 -mb-8 transform -translate-x-1/2 pointer-events-auto bg-transparent"
        aria-hidden="true"
        data-testid="popover-connector"
      />

      <div className="rounded-lg bg-white shadow-xl border border-neutral-200 flex flex-col overflow-hidden">
        <div className="overflow-y-auto flex-1 max-h-72">
          <div
            className="flex flex-col gap-1 border-b border-neutral-100 px-3 py-2 hover:bg-neutral-50"
            data-testid={`feature-item-${providerId}`}
          >
            <ProviderPopupDetails
              name={name}
              specialty={specialty}
              addressLine1={addressLine1}
              addressLine2={addressLine2}
              city={city}
              state={state}
              onNameClick={() => openProviderProfile(providerId, contentType)}
              nameTestId={`profile-link-${providerId}`}
              metricLabel={details?.volumeName}
              metrics={getProviderPopupMetrics(details)}
            />
          </div>
        </div>

        {addToList && (
          <div className="flex sticky bottom-0 z-10 items-center gap-2 border-t border-neutral-200 bg-neutral-50 px-3 py-2">
            <PulseButton
              variant="solid"
              color="primary"
              size="sm"
              onClick={handleAddToList}
              startDecorator={faPlusCircle}
              className="w-full"
              data-testid="add-selected-to-list-button"
            >
              Add to List
            </PulseButton>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <AdvancedMarker
      ref={markerRef}
      zIndex={size}
      position={position}
      onClick={handleMarkerClick}
      clickable
      className="w-9 h-9 rounded-full cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MarkerCircle
        id={`discovery-marker-circle-${providerId}`}
        sizeAsText={sizeAsText}
        isHovered={isHovered || isPopoverOpen}
      />

      {isMounted ? createPortal(popoverContent, document.body) : null}
    </AdvancedMarker>
  );
};

export default FeatureMarker;
